import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiPost, apiDelete } from '@/lib/api'

type TagMethod = 'qr' | 'nfc' | null
type ProgramState = 'idle' | 'waiting' | 'success' | 'error'

type NfcTagProgrammerProps = {
  assetId: string
  assetName: string
  existingNfcUid?: string | null
  existingQrCode?: string | null
  baseUrl?: string
  onChange?: (method: TagMethod) => void
}

async function writeNfcTag(url: string): Promise<string> {
  if (!('NDEFReader' in window)) {
    throw new Error('Web NFC not supported. Use Android Chrome, or the Capacitor app on iOS.')
  }

  const reader = new (window as any).NDEFReader()

  return new Promise((resolve, reject) => {
    const abortController = new AbortController()
    const timeout = setTimeout(() => {
      abortController.abort()
      reject(new Error('Timed out waiting for tag. Hold phone closer and try again.'))
    }, 30_000)

    reader.addEventListener('reading', ({ serialNumber }: { serialNumber: string }) => {
      clearTimeout(timeout)
      abortController.abort()
      const uid = serialNumber.replace(/:/g, '').toUpperCase()
      resolve(uid)
    }, { signal: abortController.signal })

    reader.scan({ signal: abortController.signal }).then(() => {
      return reader.write(
        { records: [{ recordType: 'url', data: url }] },
        { signal: abortController.signal },
      )
    }).catch((e: Error) => {
      clearTimeout(timeout)
      reject(e)
    })
  })
}

export default function NfcTagProgrammer({
  assetId,
  assetName,
  existingNfcUid,
  existingQrCode,
  baseUrl,
  onChange,
}: NfcTagProgrammerProps) {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<TagMethod>(
    existingNfcUid ? 'nfc' : existingQrCode ? 'qr' : null
  )
  const [programState, setProgramState] = useState<ProgramState>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [programmedUid, setProgrammedUid] = useState<string | null>(existingNfcUid ?? null)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)

  const origin = baseUrl ?? window.location.origin
  const scanUrl = `${origin}/scan/${assetId}`

  const registerMutation = useMutation({
    mutationFn: (nfcUid: string) =>
      apiPost(`/assets/nfc/assets/${assetId}/nfc-tag`, { nfcUid }),
    onSuccess: (data: any) => {
      setProgrammedUid(data.nfcUid)
      setProgramState('success')
      queryClient.invalidateQueries({ queryKey: ['asset', assetId] })
    },
    onError: (err: any) => {
      setProgramState('error')
      setErrorMsg(err.response?.data?.error ?? 'Failed to register tag')
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => apiDelete(`/assets/nfc/assets/${assetId}/nfc-tag`),
    onSuccess: () => {
      setProgrammedUid(null)
      setProgramState('idle')
      setShowRemoveConfirm(false)
      queryClient.invalidateQueries({ queryKey: ['asset', assetId] })
    },
  })

  const handleSelect = (method: TagMethod) => {
    setSelected(method)
    setProgramState('idle')
    setErrorMsg(null)
    onChange?.(method)
  }

  const handleProgram = useCallback(async () => {
    setProgramState('waiting')
    setErrorMsg(null)
    try {
      const uid = await writeNfcTag(scanUrl)
      registerMutation.mutate(uid)
    } catch (e: any) {
      setProgramState('error')
      setErrorMsg(e.message ?? 'NFC write failed')
    }
  }, [scanUrl, registerMutation])

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">Asset Tag</label>

      {/* Method selector */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => handleSelect('qr')}
          className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors ${
            selected === 'qr'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <span className="text-2xl">QR</span>
          <span className="text-sm font-medium text-gray-800">QR Code</span>
          <span className="text-xs text-gray-500 text-center">Print and stick — any camera can scan</span>
          {existingQrCode && (
            <span className="text-xs text-green-600 font-medium">Generated</span>
          )}
        </button>

        <button
          type="button"
          onClick={() => handleSelect('nfc')}
          className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors ${
            selected === 'nfc'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <span className="text-2xl">NFC</span>
          <span className="text-sm font-medium text-gray-800">NFC Tag</span>
          <span className="text-xs text-gray-500 text-center">Tap to scan — waterproof, no print needed</span>
          {programmedUid && (
            <span className="text-xs text-green-600 font-medium">Programmed</span>
          )}
        </button>
      </div>

      {/* QR panel */}
      {selected === 'qr' && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
          {existingQrCode ? (
            <div className="flex items-center gap-3">
              <div>
                <p className="text-sm font-medium text-gray-800">QR code active</p>
                <p className="text-xs text-gray-500 font-mono">{existingQrCode}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              A QR code will be generated when the asset is saved. Print and stick it to the asset.
            </p>
          )}
          <p className="text-xs text-gray-400">Scan URL: <span className="font-mono">{scanUrl}</span></p>
        </div>
      )}

      {/* NFC panel */}
      {selected === 'nfc' && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">

          {/* Already programmed */}
          {programmedUid && programState !== 'waiting' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">NFC tag programmed</p>
                  <p className="text-xs text-gray-500 font-mono break-all mt-0.5">{programmedUid}</p>
                  <p className="text-xs text-gray-400 mt-1">URL written: <span className="font-mono">{scanUrl}</span></p>
                </div>
              </div>

              {!showRemoveConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowRemoveConfirm(true)}
                  className="text-xs text-gray-500 hover:text-red-600 underline"
                >
                  Replace / remove tag
                </button>
              ) : (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-2">
                  <p className="text-xs text-red-700 font-medium">Remove this NFC tag from "{assetName}"?</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowRemoveConfirm(false)}
                      className="flex-1 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => removeMutation.mutate()}
                      disabled={removeMutation.isPending}
                      className="flex-1 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300"
                    >
                      {removeMutation.isPending ? 'Removing...' : 'Remove Tag'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Program prompt */}
          {!programmedUid && programState === 'idle' && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600 space-y-1">
                <p>Hold an on-metal NFC tag against the back of your phone to write the asset URL.</p>
                <p className="text-xs text-gray-400">
                  Requires Android Chrome. On iOS, use the EstateKey Capacitor app.
                </p>
              </div>
              <button
                type="button"
                onClick={handleProgram}
                className="w-full py-3 bg-navy-800 text-white rounded-lg text-sm font-medium hover:bg-navy-700"
              >
                Program NFC Tag
              </button>
            </div>
          )}

          {/* Waiting for tag */}
          {programState === 'waiting' && (
            <div className="text-center space-y-3 py-2">
              <div className="text-4xl animate-pulse">NFC</div>
              <p className="text-sm font-medium text-gray-800">Hold tag to the back of your phone...</p>
              <p className="text-xs text-gray-500">Writing: <span className="font-mono">{scanUrl}</span></p>
              <button
                type="button"
                onClick={() => setProgramState('idle')}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Success */}
          {programState === 'success' && programmedUid && (
            <div className="flex items-start gap-3">
              <div>
                <p className="text-sm font-medium text-green-800">Tag programmed successfully</p>
                <p className="text-xs text-gray-500 font-mono break-all mt-0.5">{programmedUid}</p>
              </div>
            </div>
          )}

          {/* Error */}
          {programState === 'error' && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-2">
              <p className="text-sm text-red-700">{errorMsg}</p>
              <button
                type="button"
                onClick={() => { setProgramState('idle'); setErrorMsg(null) }}
                className="text-xs text-red-600 underline hover:text-red-800"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
